import * as Sentry from '@sentry/react';
import { useCoreTranslations } from '@solinkcorp/authentication';
import React from 'react';
import { trackErrorBoundary } from 'src/core/analytics';
import { IS_PRODUCTION } from 'src/environment';
import ErrorFallback, { FullPageWrapper } from './ErrorFallback';

interface State {
  error: Error | undefined;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryLocale {
  unexpectedError: string;
}

interface ErrorBoundaryInnerProps extends ErrorBoundaryProps {
  locale: ErrorBoundaryLocale;
}

/**
 * Wraps a component - if the component or any of its descendants throws an error
 * while rendering, this will replace the wrapped component with a generic
 * "Sorry - try again later.  Let us know if this keeps happening," screen.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryInnerProps, State> {
  constructor(props: ErrorBoundaryInnerProps) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to Mixpanel and Intercom
    trackErrorBoundary(error, errorInfo.componentStack);

    // Log error to Sentry
    if (IS_PRODUCTION) {
      Sentry.captureException(error, {
        tags: {
          error_type: 'sadface',
        },
        extra: {
          componentStack: errorInfo.componentStack,
        },
      });
    }
  }

  render() {
    const { error } = this.state;

    if (error) {
      return (
        <FullPageWrapper>
          <ErrorFallback message={this.props.locale.unexpectedError} />
        </FullPageWrapper>
      );
    }
    return this.props.children;
  }
}

const ComposedErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const locale = useCoreTranslations();
  return <ErrorBoundary {...props} locale={locale} />;
};
export default ComposedErrorBoundary;
